import React, { useState, useEffect, useReducer, useCallback } from 'react';
import { Upload as AntUpload, message } from 'antd';
import { t } from 'ttag';
import { InboxOutlined } from '@ant-design/icons';

import { Status } from '../../services/image-service';
import type * as ImageService from '../../services/image-service';
import { formatBytes } from '../../services/file-service';

import Preview from './Preview';
import ImageCrop, { Crop } from '../ImageCrop';

enum Actions {
  ImagePopulate,
  ImageReset,
  HideCropModal,
  ShowCropModal,
}

export const TYPE_PERSON_PROFILE_PIC = 'profile';

interface State {
  cropSource: string;
  imageCropVisible: boolean;
  currentImageId: string;
  currentImageUrl: string;
  naturalWidth: number;
  naturalHeight: number;
}

const defaultState: State = {
  cropSource: null,
  imageCropVisible: false,
  currentImageId: null,
  currentImageUrl: null,
  naturalWidth: null,
  naturalHeight: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case Actions.ImagePopulate: {
      return {
        ...state,
        imageCropVisible: false,
        currentImageId: action.payload.imageId,
        currentImageUrl: action.payload.url,
        naturalHeight: action.payload?.naturalHeight,
        naturalWidth: action.payload?.naturalWidth,
      };
    }

    case Actions.ImageReset: {
      return {
        ...state,
        ...defaultState,
      };
    }

    case Actions.ShowCropModal: {
      return {
        ...state,
        imageCropVisible: true,
        cropSource: action.payload.src,
        currentImageId: action.payload.imageId,
      };
    }

    case Actions.HideCropModal: {
      if (typeof action.payload !== 'undefined') {
        return {
          ...state,
          imageCropVisible: false,
          cropSource: null,
          currentImageId: action.payload.imageId,
          currentImageUrl: action.payload.url,
        };
      }

      return {
        ...state,
        cropSource: null,
        imageCropVisible: false,
      };
    }
    default:
      return { ...defaultState };
  }
};

const { Dragger } = AntUpload;

const ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/bmp', 'image/webp'];
const MAXIMUM_ALLOWED_FILE_SIZE = 1024 * 1024 * 24;

const STATUS_DONE = 'done';
const STATUS_ERROR = 'error';
const STATUS_UPLOADING = 'uploading';

interface Value {
  id: string;
}

export interface Props {
  cropSettings?: Crop;
  enableCrop?: boolean;
  resourceId: string;
  resourceType: ImageService.ResourceType;
  height?: number;
  width?: number;
  onComplete?: (value: Value & { imageUrl?: string }) => void;
  policyName: ImageService.EntityType;
  // If we're displaying an existing image, this should have the image ID
  value?: Value;
  // Token is used by SelfEditProfile to authenticate the upload
  token?: string;
  allowGif?: boolean;
  // Additional information to be displayed after the max size text
  information?: string;
  // Render the preview with the original image dimensions or scaled down
  originalDimensions?: boolean;
  circularCrop?: boolean;
  cropFn: typeof ImageService.crop;
  getFn: typeof ImageService.get;
  createFn: typeof ImageService.create;
  uploadFn: typeof ImageService.upload;
  notifyFn: typeof ImageService.notify;
}

const ImageUpload = ({
  resourceId,
  resourceType,
  cropSettings = {},
  enableCrop = false,
  height = 200,
  onComplete = () => {},
  policyName,
  value: { id: imageId } = { id: null },
  width = 200,
  token = '',
  allowGif = false,
  information = '',
  originalDimensions = false,
  circularCrop = false,
  getFn,
  cropFn,
  createFn,
  uploadFn,
  notifyFn,
}: Props) => {
  const [state, dispatch] = useReducer(reducer, defaultState);

  const [status, setStatus] = useState(null);

  // not all browsers we support have event.target but all of them have event.composedPath function
  const extractTarget = useCallback(e => {
    if (typeof e.target !== 'undefined') {
      return e.target;
    }

    if (typeof e.composedPath === 'function') {
      const [target] = e.composedPath();
      return target;
    }
    return {};
  }, []);

  const effect = useCallback(async () => {
    try {
      const imageParams = originalDimensions
        ? {}
        : {
            scale_width: width,
            scale_height: height,
            user_crop: true,
            fit_in: true,
            fill_color: '#FFFFFF',
          };
      const image = await getFn(resourceId, imageId, imageParams, token, resourceType);

      const onLoad = event => {
        const target = extractTarget(event);
        const { naturalHeight = height, naturalWidth = width } = target;
        dispatch({
          type: Actions.ImagePopulate,
          payload: { imageId, url: image.imageUrl, naturalHeight, naturalWidth },
        });
      };

      if (image?.imageUrl) {
        const img = new Image();
        img.src = image.imageUrl;
        img.onload = onLoad;
        return;
      }

      // dispatch with empty values if imageUrl is null
      dispatch({
        type: Actions.ImagePopulate,
        payload: { imageId, url: image.imageUrl },
      });
    } catch {
      message.error(t`Image load failed.`);
    }
    // TODO Remove the eslint-disable and fix the problem
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageId, resourceId, resourceType, token, width, height, originalDimensions]);

  useEffect(() => {
    // When the crop window closes this effect should be called
    if (resourceId && imageId && !state.imageCropVisible) {
      effect();
    }
  }, [resourceId, resourceType, imageId, state.imageCropVisible, effect]);

  const onChange = info => {
    const { file } = info;

    if (file.status === STATUS_DONE) {
      message.success(t`${info.file.name} uploaded successfully.`);
    } else if (file.status === STATUS_ERROR) {
      message.error(t`${info.file.name} upload failed.`);
    }

    setStatus(file.status);
  };

  const onRequest = async request => {
    const { file } = request;
    const onProgress = (progress: ImageService.UploadProgressEvent) =>
      request.onProgress({ percent: (progress.loaded / progress.total) * 100 });

    const { id: imageUploadId, upload } = await createFn(
      resourceId,
      file.name,
      file.type,
      file.size,
      policyName,
      token,
      resourceType,
    );

    try {
      request.onSuccess(await uploadFn(upload.url, file, onProgress));
    } catch (err) {
      request.onError(err);
    }

    const { imageUrl, id } = await notifyFn(
      resourceId,
      imageUploadId,
      Status.Finalized,
      token,
      resourceType,
    );

    const onLoad = event => {
      const target = extractTarget(event);

      const { naturalHeight = height, naturalWidth = width } = target;
      dispatch({
        type: Actions.ImagePopulate,
        payload: { imageId: id, url: imageUrl, naturalHeight, naturalWidth },
      });

      onComplete({ id, imageUrl });
    };

    const img = new Image();
    img.src = imageUrl;
    img.onload = onLoad;
  };

  const onReset = () => {
    setStatus(null);
    dispatch({ type: Actions.ImageReset });
    onComplete({ id: null });
  };

  const maxFileSize = formatBytes(MAXIMUM_ALLOWED_FILE_SIZE);

  const beforeUpload = (file, fileList) => {
    // validation for file size
    if (file.size > MAXIMUM_ALLOWED_FILE_SIZE) {
      message.error(t`${file.name} exceeds maximum size limit (${maxFileSize})`);

      // eslint-disable-next-line no-param-reassign
      file.status = 'error';

      return false;
    }

    // validation for unsupported file types
    if (!(ALLOWED_MIME_TYPES.includes(file.type) || (allowGif && file.type === 'image/gif'))) {
      message.error(t`${file.name} is not an image`);

      fileList.splice(fileList.indexOf(file), 1);
      return false;
    }

    return true;
  };

  const onCrop = async cropPayload => {
    try {
      const croppedImage = await cropFn(
        resourceId,
        state.currentImageId,
        cropPayload,
        token,
        resourceType,
      );
      dispatch({
        type: Actions.HideCropModal,
        payload: { imageId: croppedImage.id, url: croppedImage.imageUrl },
      });
      onComplete({ id: state.currentImageId, imageUrl: croppedImage.imageUrl });
    } catch {
      message.error(t`Error cropping image.`);
    }
  };

  const onCropClick = async () => {
    await getFn(resourceId, imageId, { crop: 'no_crop' }, token, resourceType).then(image =>
      dispatch({
        type: Actions.ShowCropModal,
        payload: { src: image.imageUrl, imageId: image.id },
      }),
    );
  };

  return (
    <>
      {status !== STATUS_DONE && state.currentImageUrl == null ? (
        <Dragger
          showUploadList
          customRequest={onRequest}
          beforeUpload={beforeUpload}
          accept={
            allowGif
              ? `image/jpeg,.jpeg,.jpg,image/png,.png,image/bmp,.bmp,image/webp,.webp,.gif`
              : `image/jpeg,.jpeg,.jpg,image/png,.png,image/bmp,.bmp,image/webp,.webp`
          }
          onChange={onChange}
          disabled={status === STATUS_DONE || status === STATUS_UPLOADING}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">{t`Click or drag file to this area to upload`}</p>
          <p className="ant-upload-hint">
            {allowGif
              ? t`Supported file types: *.jpeg, *.jpg, *.png, *.bmp, *.webp and *.gif`
              : t`Supported file types: *.jpeg, *.jpg, *.png, *.bmp and *.webp`}
          </p>
          <p className="ant-upload-hint">
            {t`The maximum file size per image is ${maxFileSize}.`} {information}
          </p>
        </Dragger>
      ) : (
        <Preview
          image={state.currentImageUrl}
          enableCrop={enableCrop}
          height={originalDimensions ? state.naturalHeight : height}
          onDelete={onReset}
          onCropClick={onCropClick}
          width={originalDimensions ? state.naturalWidth : width}
        />
      )}
      {state.imageCropVisible && (
        <ImageCrop
          visible
          src={state.cropSource}
          settings={cropSettings}
          onCropSaveClick={onCrop}
          onModalClose={() => dispatch({ type: Actions.HideCropModal })}
          circularCrop={circularCrop}
        />
      )}
    </>
  );
};

export default ImageUpload;
